import React, { FunctionComponent } from 'react'
import { AppliedFilter } from '../../entities/Filter'
import style from './attributeFilters.css'
import ResetFilterIcon from '../../assets/media/icons/reset-filters.svg'
import FilterOption from '../../entities/FilterOption'
import RangeSlider from '../range-slider/RangeSlider'
import AttributeFiltersPlaceholder from './AttributeFiltersPlaceholder'
import getFilterTranslation from '../../helpers/getFilterTranslation'
import { FiltersState } from '../../redux/reducers/filtersReducer'
import Select from 'react-select'
import { OptionType } from '../../entities/OptionType'

export type Props = {
    filterOptions: FilterOption[]
    applyFilter: (filter: AppliedFilter) => void
    removeFilter: (filterId: string) => void
    isLoading: boolean
    layout: 'vertical' | 'horizontal'
    resetFilters: () => void
    selectedFilters: FiltersState
    translationDebugEnabled?: boolean
}

const AttributeFilters: FunctionComponent<Props> = (props) => {
    const {
        filterOptions,
        applyFilter,
        removeFilter,
        isLoading,
        layout,
        resetFilters,
        selectedFilters,
        translationDebugEnabled,
    } = props

    const t = (key: string, params?: {}) => getFilterTranslation(key, params, translationDebugEnabled)

    const handleSelectChange = (selectedOption: OptionType | null, attribute: string, activeValues?: (string | number)[]): void => {
        if (activeValues && activeValues.length > 0) {
            removeFilter(attribute)
        }

        if (selectedOption) {
            applyFilter({ id: attribute, value: selectedOption.value, type: 'multiple' })
        }
    }

    const selectStyle = {
            control: (base: any, state: any) => ({
                ...base,
                border: '1px solid var(--color-grey-30)',
                borderRadius: '4px',
                boxShadow: state.isFocused ? '0 0 0 1px var(--color-grey-40)' : 'none',
                '&:hover': {
                    border: '1px solid var(--color-grey-40)',
                },
            }),
            option: (base: any, { isFocused, isSelected }: any) => ({
                ...base,
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                backgroundColor: isFocused ? 'var(--color-grey-15)' : 'transparent',
                color: isSelected ? 'var(--color-black)' : 'var(--color-grey-80)',
                cursor: 'pointer',
                ':active': {
                    backgroundColor: 'var(--color-grey-15)',
                },
            }),
            clearIndicator: (base: any) => ({
                ...base,
            }),
        }

    const renderSelectFilter = (filterOption: FilterOption) => {
        const { attribute, values, activeValues } = filterOption
        const validAttribute = attribute.replaceAll('%', '').replaceAll('.', '')

        const options = (values as string[]).map((value) => ({
            value,
            label: t(`attributes.valueLabels.${validAttribute}.${value}`),
        }))

        const placeholder = t(`filters.filterLabels.${validAttribute}`)

        const selectedOption = activeValues && activeValues.length > 0
            ? options.find(option => option.value === activeValues[0])
            : null

        const hasOptions = options.length > 0
        const inputId = `filter-${attribute}`
        const displayPlaceholder = hasOptions ? t('filters.filterLabels.selectOption') : t('filters.filterLabels.noOptionsAvailable')

        return (
            <div key={attribute}>
                <label
                    htmlFor={inputId}
                    className={style.title}
                    data-testid={`filter-label-${validAttribute}`}
                >
                    {placeholder}
                </label>
                <Select
                    inputId={inputId}
                    options={options}
                    value={selectedOption}
                    placeholder={displayPlaceholder}
                    isClearable={hasOptions}
                    isDisabled={!hasOptions}
                    closeMenuOnSelect={true}
                    blurInputOnSelect={true}
                    styles={selectStyle}
                    menuShouldScrollIntoView={true}
                    onChange={(option: OptionType | null) => handleSelectChange(option, attribute, activeValues)}
                />
            </div>
        )
    }

    const filterElements = (filterOptions.length === 0 && isLoading)
        ? <AttributeFiltersPlaceholder/>
        : filterOptions.map((filterOption) => {
            const {
                attribute,
                type,
                values,
                activeValues,
            } = filterOption

            if (type === 'multiple') {
                return renderSelectFilter(filterOption)
            }

            if (type === 'max') {
                const numericValues = (values as (string | number)[])
                    .map(v => typeof v === 'string' ? parseFloat(v) : v)
                    .filter(v => typeof v === 'number' && !isNaN(v))
                    .sort((a, b) => a - b)

                if (numericValues.length === 0) {
                    return null
                }

                const minValue = Math.min(...numericValues)
                const maxValue = Math.max(...numericValues)
                const extendedValues = [...numericValues, maxValue + 1]
                const hasActiveFilter = activeValues && activeValues.length > 0
                const activeFilterValue = hasActiveFilter ? activeValues[0] as number : maxValue + 1
                const initialValue = activeFilterValue > maxValue ? maxValue + 1 : activeFilterValue

                return (
                    <label key={attribute}>
                        <span className={style.title}>{t(`filters.filterLabels.${attribute}-${type}`)}</span>
                        <RangeSlider
                            initial={initialValue}
                            min={minValue}
                            max={maxValue + 1}
                            values={extendedValues}
                            onChange={(selectedValue) => {
                                if (selectedValue === maxValue + 1) {
                                    removeFilter(attribute)
                                } else {
                                    applyFilter({
                                        id: attribute,
                                        value: selectedValue,
                                        type,
                                    })
                                }
                            }}
                            debounceTime={200}
                            suffix={t(`filters.filterValueSuffix.${attribute}`)}
                            allLabel="*"
                            hasAllSection={true}
                            dataTestId={`attribute-filter-${attribute}`}
                        />
                    </label>
                )
            }

            return null
        })

    const wrapperClassName = style[`wrapper-${layout}`]
    const resetFiltersEnabled = selectedFilters &&
        Object.values(selectedFilters).some(arr => Array.isArray(arr) && arr.length > 0)

    const resetFiltersButton = React.Children.toArray(filterElements).length > 0 ? <button
        disabled={!resetFiltersEnabled}
        className={`${!resetFiltersEnabled ? style.disabledResetFilter : ''} ${style.resetFilters}`}
        onClick={resetFilters}
        data-testid="reset-filters"
    >
        <ResetFilterIcon />
        {filterOptions.length === 0 && isLoading
            ? t('loading')
            : t('filters.resetButtonLabel')}
    </button>
        : null

    return (
        <div className={style.filters}>
            <div className={style.resetButtonContainer}>
                {resetFiltersButton}
            </div>
            <div className={wrapperClassName}>
                {filterElements}
            </div>
        </div>
    )
}

export default AttributeFilters
